import { z } from "zod";
import { BillboardId, Cents, IsoUtc, PropertyId, Uuid } from "./primitives";
import { HomeStageId } from "./world";
import { CAMPAIGN_FUNDING_MAX_CENTS, CAMPAIGN_FUNDING_MIN_CENTS } from "./rewards";

/**
 * Dollar commerce (M4, docs/04, docs/06 section 4, ADR-0007, ADR-0046). Server-priced quotes, idempotent
 * orders with reservations and credit holds, Stripe PaymentIntents (test or live mode, decided by the
 * server's configuration) for the card shortfall, Stripe Tax on top of the tax-exclusive price,
 * webhook-driven fulfillment, and original-method refunds for unfulfilled orders.
 *
 * Nothing here is authority: the client never sends a price, tax, balance, owner, mode or "paid" flag.
 * Coin purchases keep their existing endpoints (real estate, catalog, home upgrade).
 */

export const QuoteKind = z.enum(["COMMERCIAL_ACQUISITION", "RESIDENTIAL_PLOT", "HOME_UPGRADE", "CATALOG_ITEM", "BILLBOARD_RENTAL", "CAMPAIGN_FUNDING"]);
export type QuoteKind = z.infer<typeof QuoteKind>;

/** ADR-0046: "test" = Stripe test mode (no real money); "live" = real card payments. Set by the server only. */
export const PaymentMode = z.enum(["test", "live"]);
export type PaymentMode = z.infer<typeof PaymentMode>;

/**
 * ADR-0046: where the buyer is, for sales tax (Stripe Tax). ISO 3166-1 alpha-2 country; a postal code (US: 5-digit ZIP
 * or ZIP+4; required for US and CA). Sent with a quote when the server asks for it (`taxAddressRequired`).
 */
export const TaxAddress = z
  .object({
    country: z.string().regex(/^[A-Za-z]{2}$/, "Choose a country"),
    postalCode: z
      .string()
      .trim()
      .max(12)
      .regex(/^([A-Za-z0-9][A-Za-z0-9 -]{0,11})?$/, "Enter a postal code with letters, digits, spaces or dashes")
      .nullable()
      .optional(),
  })
  .strict();
export type TaxAddress = z.infer<typeof TaxAddress>;

/** POST /api/v1/quotes. `useCredit` applies the buyer's available Townsy Credit first (default true). */
export const QuoteRequest = z.discriminatedUnion("kind", [
  z
    .object({
      kind: z.literal("COMMERCIAL_ACQUISITION"),
      propertyId: PropertyId,
      /** The property version the buyer looked at; a different current version returns PROPERTY_VERSION_CHANGED. */
      expectedPropertyVersion: z.number().int().min(0),
      useCredit: z.boolean().default(true),
      taxAddress: TaxAddress.optional(),
    })
    .strict(),
  z.object({ kind: z.literal("RESIDENTIAL_PLOT"), propertyId: PropertyId, useCredit: z.boolean().default(true), taxAddress: TaxAddress.optional() }).strict(),
  z.object({ kind: z.literal("HOME_UPGRADE"), propertyId: PropertyId, toStage: HomeStageId, useCredit: z.boolean().default(true), taxAddress: TaxAddress.optional() }).strict(),
  z.object({ kind: z.literal("CATALOG_ITEM"), itemId: z.string().min(1).max(64), useCredit: z.boolean().default(true), taxAddress: TaxAddress.optional() }).strict(),
  /** M5: exclusive whole UTC days (docs/04 section 10): start no earlier than the next UTC day, 7 or 30 days, an APPROVED billboard creative of the buyer. */
  z
    .object({
      kind: z.literal("BILLBOARD_RENTAL"),
      billboardId: BillboardId,
      startDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
      termDays: z.union([z.literal(7), z.literal(30)]),
      creativeId: Uuid,
      useCredit: z.boolean().default(true),
      taxAddress: TaxAddress.optional(),
    })
    .strict(),
  /**
   * M6 (ADR-0011 #2, docs/04 section 2): fund the cents budget of the buyer's own sponsored campaign. CARD ONLY:
   * Townsy Credit can never fund a campaign (useCredit is fixed to false; the database refuses credit on these orders).
   */
  z
    .object({
      kind: z.literal("CAMPAIGN_FUNDING"),
      campaignId: Uuid,
      amountCents: z.number().int().min(CAMPAIGN_FUNDING_MIN_CENTS).max(CAMPAIGN_FUNDING_MAX_CENTS),
      useCredit: z.literal(false).default(false),
      taxAddress: TaxAddress.optional(),
    })
    .strict(),
]);
export type QuoteRequest = z.infer<typeof QuoteRequest>;

/** One sales-tax line (a jurisdiction) of a quote or receipt. */
export const TaxLineView = z.object({
  /** e.g. "Sales tax (WA) 10.25%", or "No sales tax collected (US OR)". */
  label: z.string(),
  amountCents: Cents,
  taxableAmountCents: Cents,
  /** Percent as a decimal string ("10.25"); null when unknown. */
  ratePercent: z.string().nullable(),
});
export type TaxLineView = z.infer<typeof TaxLineView>;

/** ADR-0046: how the tax on a quote/order was determined. Tax is never paid with Townsy Credit. */
export const TaxSummaryView = z.object({
  /** "stripe_tax": calculated by Stripe Tax at the buyer's location; "none": no tax calculation (Stripe Tax off; test mode only). */
  source: z.enum(["stripe_tax", "none"]),
  country: z.string().nullable(),
  postalCode: z.string().nullable(),
  lines: z.array(TaxLineView),
});
export type TaxSummaryView = z.infer<typeof TaxSummaryView>;

/** An immutable, expiring price snapshot. Not a reservation: the property is checked again when ordering. */
export const QuoteResponse = z.object({
  quoteId: Uuid,
  kind: QuoteKind,
  propertyId: PropertyId.nullable(),
  propertyVersion: z.number().int().min(0).nullable(),
  itemId: z.string().nullable(),
  toStage: HomeStageId.nullable(),
  /** Human label, e.g. "12 Main Street" or "Backyard pool". */
  description: z.string(),
  currency: z.literal("USD"),
  subtotalCents: Cents,
  /**
   * Sales tax on top of the tax-exclusive subtotal (ADR-0046: Stripe Tax; 0 when Stripe Tax is off, which only test mode
   * allows). Paid by card only: credit covers at most the subtotal, so card = subtotal - credit + tax (docs/04 section 2).
   */
  taxCents: Cents,
  creditAppliedCents: Cents,
  cardAmountCents: Cents,
  /** ADR-0046: the tax lines and the location they were calculated for. */
  tax: TaxSummaryView.optional(),
  /** Commercial only: the basis the buyer will hold (reimbursed in full if later displaced). */
  reimbursableBasisCents: Cents.nullable(),
  /** Commercial only: the current owner's public label, or null for an unowned/demo store. */
  currentOwnerLabel: z.string().nullable(),
  /** Commercial only: the current owner receives this much Townsy Credit when the takeover completes (0 for demo). */
  displacementCreditCents: Cents.nullable(),
  /** M6 campaign funding only: the funded campaign. */
  campaignId: Uuid.nullable().optional(),
  expiresAt: IsoUtc,
  pricingPolicyVersion: z.number().int().min(1),
  termsVersion: z.string(),
  mode: PaymentMode,
});
export type QuoteResponse = z.infer<typeof QuoteResponse>;

/** POST /api/v1/orders (requires Idempotency-Key; same key + same body returns the same order, a different body 409). */
export const OrderCreateRequest = z.object({ quoteId: Uuid }).strict();
export type OrderCreateRequest = z.infer<typeof OrderCreateRequest>;

/** docs/04 section 8. */
export const OrderStatus = z.enum([
  "created",
  "reserved",
  "awaiting_payment",
  "payment_confirmed",
  "fulfilled",
  "expired",
  "canceled",
  "compensation_required",
  "refund_pending",
  "refunded",
  "manual_review",
]);
export type OrderStatus = z.infer<typeof OrderStatus>;

/** The provider's view of the card payment, tracked separately from the order status. */
export const ProviderPaymentStatus = z.enum([
  "none",
  "requires_payment_method",
  "requires_confirmation",
  "requires_action",
  "processing",
  "succeeded",
  "canceled",
  "unknown",
]);
export type ProviderPaymentStatus = z.infer<typeof ProviderPaymentStatus>;

export const RefundStatus = z.enum(["none", "pending", "requires_action", "succeeded", "failed", "canceled"]);
export type RefundStatus = z.infer<typeof RefundStatus>;

export const OrderView = z.object({
  orderId: Uuid,
  kind: QuoteKind,
  status: OrderStatus,
  propertyId: PropertyId.nullable(),
  itemId: z.string().nullable(),
  toStage: HomeStageId.nullable(),
  /** M5 billboard rentals: the board and the [start, end) UTC dates; absent/null for other kinds. */
  billboard: z.object({ billboardId: BillboardId, startDate: z.string(), endDate: z.string() }).nullable().optional(),
  /** M6 campaign funding only: the campaign whose budget this order funds. */
  campaignId: Uuid.nullable().optional(),
  description: z.string(),
  currency: z.literal("USD"),
  subtotalCents: Cents,
  taxCents: Cents,
  creditAppliedCents: Cents,
  cardAmountCents: Cents,
  /** ADR-0046: the receipt's tax lines (card-paid; never Townsy Credit). */
  tax: TaxSummaryView.optional(),
  /** While the property/credit is held for this order. */
  reservationExpiresAt: IsoUtc.nullable(),
  paymentStatus: ProviderPaymentStatus,
  refund: z.object({ status: RefundStatus, amountCents: Cents }),
  /** Plain-language state for the UI (e.g. "Waiting for your card payment", "Refund pending at your bank"). */
  message: z.string(),
  receiptNumber: z.string().nullable(),
  createdAt: IsoUtc,
  fulfilledAt: IsoUtc.nullable(),
  /** The mode the order was placed in (a test-mode order stays test mode after the server goes live). */
  mode: PaymentMode,
});
export type OrderView = z.infer<typeof OrderView>;

export const OrderListResponse = z.object({ items: z.array(OrderView), nextCursor: z.string().nullable() });
export type OrderListResponse = z.infer<typeof OrderListResponse>;

/** POST /api/v1/orders/:id/payment-intent — create or reuse this buyer's PaymentIntent for the fixed card amount. */
export const PaymentIntentResponse = z.object({
  orderId: Uuid,
  /** Only ever returned to the order's buyer; never logged. */
  clientSecret: z.string().min(10),
  /** pk_test_ in test mode, pk_live_ in live mode (the server's configured key; never chosen by the browser). */
  publishableKey: z.string().regex(/^pk_(test|live)_/),
  cardAmountCents: Cents,
  currency: z.literal("USD"),
  reservationExpiresAt: IsoUtc.nullable(),
  mode: PaymentMode,
});
export type PaymentIntentResponse = z.infer<typeof PaymentIntentResponse>;

/** GET /api/v1/payments/config (public). */
export const PaymentsConfigResponse = z.object({
  /** Dollar purchases available right now (provider configured and the feature flag on). */
  enabled: z.boolean(),
  provider: z.enum(["disabled", "simulated", "stripe"]),
  /** ADR-0046: "live" only on production with live payments switched on; test cards and "test" labels otherwise. */
  mode: PaymentMode,
  publishableKey: z.string().nullable(),
  commercialTakeoversEnabled: z.boolean(),
  /** ADR-0046: Stripe Tax is on: the checkout asks for the buyer's country and postal code before the price is fixed. */
  taxAddressRequired: z.boolean().optional(),
  /** Honest copy shown next to every price (test mode: no real money moves). */
  notice: z.string(),
});
export type PaymentsConfigResponse = z.infer<typeof PaymentsConfigResponse>;

/** Notification payload when a takeover displaced you (also visible in wallet history). */
export const DisplacementNotice = z.object({
  propertyId: PropertyId,
  address: z.string(),
  creditGrantedCents: Cents,
  at: IsoUtc,
});
export type DisplacementNotice = z.infer<typeof DisplacementNotice>;
